// =============================================================================
// Usage, limits & billing UI — see docs/48. Two meters (AI credits + voice
// minutes), each with a master switch, a PAYG opt-in, and prepaid pack buy
// buttons. Prices are NOT shown here — they live in Stripe and appear at
// checkout (quantities are the only code constants).
// =============================================================================

import { JarvisCard } from '@/components/JarvisCard'
import {
  useAiUsage,
  useVoiceUsage,
  useBuyPack,
  useUpdateAccountFlags,
  type PackId,
} from '@/lib/hooks'

function barColor(pct: number): string {
  if (pct >= 100) return 'bg-jarvis-red'
  if (pct >= 80) return 'bg-jarvis-amber'
  return 'bg-jarvis-blue'
}

function ProgressBar({ used, total }: { used: number; total: number }) {
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : used > 0 ? 100 : 0
  return (
    <div className="h-2 w-full rounded-full bg-jarvis-surface-2 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${barColor(pct)}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

function Toggle({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  hint?: string
  disabled?: boolean
}) {
  return (
    <label className="flex items-start gap-3 text-sm">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-jarvis-blue mt-0.5"
      />
      <span>
        <span className="text-jarvis-text">{label}</span>
        {hint && <span className="block text-xs text-jarvis-muted mt-0.5">{hint}</span>}
      </span>
    </label>
  )
}

function PackButtons({
  packs,
  onBuy,
  pending,
}: {
  packs: Array<{ id: PackId; label: string }>
  onBuy: (id: PackId) => void
  pending: boolean
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {packs.map((p) => (
        <button
          key={p.id}
          onClick={() => onBuy(p.id)}
          disabled={pending}
          className="bg-jarvis-surface-2 border border-jarvis-border hover:border-jarvis-blue/60 disabled:opacity-50 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
        >
          {p.label}
        </button>
      ))}
    </div>
  )
}

const OVERAGE_HINT =
  "You'll use your plan's included amount, then any prepaid balance. " +
  'Turn this on to keep going on pay-as-you-go after that; otherwise Jarvis ' +
  'stops until next month or your next pack.'

export function UsageSettings() {
  const ai = useAiUsage()
  const voice = useVoiceUsage()
  const buyPack = useBuyPack()
  const flags = useUpdateAccountFlags()

  return (
    <JarvisCard>
      <h2 className="text-xs uppercase tracking-widest text-jarvis-muted mb-4">Usage &amp; Limits</h2>

      {/* ---- AI credits ---- */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h3 className="text-sm font-semibold">AI credits</h3>
          {ai.data && (
            <span className="text-xs text-jarvis-muted">
              {ai.data.usedCredits.toLocaleString()} / {ai.data.includedCredits.toLocaleString()}{' '}
              included
              {ai.data.topupCredits > 0 && ` · +${ai.data.topupCredits.toLocaleString()} prepaid`}
            </span>
          )}
        </div>

        {ai.isLoading && <p className="text-xs text-jarvis-muted">Loading…</p>}
        {ai.isError && <p className="text-xs text-jarvis-red">Couldn’t load AI usage.</p>}
        {ai.data && (
          <>
            <ProgressBar used={ai.data.usedCredits} total={ai.data.includedCredits} />
            <p className="text-xs text-jarvis-muted">
              {ai.data.remainingCredits.toLocaleString()} credits remaining this period
              {ai.data.costUsd > 0 && ` · ~$${ai.data.costUsd.toFixed(2)} est. cost`}
            </p>
            {ai.data.remainingCredits === 0 && !ai.data.overageOptIn && (
              <p className="text-xs text-jarvis-amber">
                AI limit reached — turn on overage or add a credit pack for more.
              </p>
            )}

            <div className="space-y-2 pt-1">
              <Toggle
                label="AI features"
                hint="Master switch — turn off to block all AI for this account."
                checked={ai.data.aiEnabled}
                disabled={flags.isPending}
                onChange={(v) => flags.mutate({ aiEnabled: v })}
              />
              <Toggle
                label="Allow AI overage billing"
                hint={`Keep using AI past your credits at $${ai.data.overageUsdPerCredit.toFixed(2)}/credit. ${OVERAGE_HINT}`}
                checked={ai.data.overageOptIn}
                disabled={flags.isPending}
                onChange={(v) => flags.mutate({ aiOverageOptIn: v })}
              />
            </div>

            <PackButtons
              pending={buyPack.isPending}
              onBuy={(id) => buyPack.mutate(id)}
              packs={[
                { id: 'credits_1k', label: '+1,000 credits' },
                { id: 'credits_5k', label: '+5,000 credits' },
                { id: 'credits_20k', label: '+20,000 credits' },
              ]}
            />
          </>
        )}
      </section>

      <div className="border-t border-jarvis-border my-5" />

      {/* ---- Voice minutes ---- */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h3 className="text-sm font-semibold">Voice minutes</h3>
          {voice.data && (
            <span className="text-xs text-jarvis-muted">
              {voice.data.used.toLocaleString()} / {voice.data.included.toLocaleString()} included
              {voice.data.topup > 0 && ` · +${voice.data.topup.toLocaleString()} prepaid`}
            </span>
          )}
        </div>

        {voice.isLoading && <p className="text-xs text-jarvis-muted">Loading…</p>}
        {voice.isError && <p className="text-xs text-jarvis-red">Couldn’t load voice usage.</p>}
        {voice.data && (
          <>
            <ProgressBar used={voice.data.used} total={voice.data.included} />
            <p className="text-xs text-jarvis-muted">
              {voice.data.remaining.toLocaleString()} minutes remaining
            </p>
            {voice.data.remaining === 0 && !voice.data.overageOptIn && (
              <p className="text-xs text-jarvis-amber">
                Out of minutes — turn on overage or add a minute pack to keep calling.
              </p>
            )}

            <div className="space-y-2 pt-1">
              <Toggle
                label="Voice calls"
                hint="Master switch — turn off to block all calls for this account."
                checked={voice.data.voiceEnabled}
                disabled={flags.isPending}
                onChange={(v) => flags.mutate({ voiceEnabled: v })}
              />
              <Toggle
                label="Allow voice overage billing"
                hint={`Keep taking calls past your minutes on pay-as-you-go. ${OVERAGE_HINT}`}
                checked={voice.data.overageOptIn}
                disabled={flags.isPending}
                onChange={(v) => flags.mutate({ minuteOverageOptIn: v })}
              />
            </div>

            <PackButtons
              pending={buyPack.isPending}
              onBuy={(id) => buyPack.mutate(id)}
              packs={[
                { id: 'minutes_100', label: '+100 minutes' },
                { id: 'minutes_500', label: '+500 minutes' },
                { id: 'minutes_1000', label: '+1,000 minutes' },
              ]}
            />
          </>
        )}
      </section>
    </JarvisCard>
  )
}

/** Compact credits + minutes chip for the Command Center header. */
export function UsageChip() {
  const ai = useAiUsage()
  const voice = useVoiceUsage()
  if (!ai.data && !voice.data) return null
  return (
    <a
      href="/settings#usage"
      className="inline-flex items-center gap-3 text-xs text-jarvis-muted hover:text-jarvis-text transition-colors"
      title="AI credits and voice minutes remaining — manage in Settings"
    >
      {ai.data && (
        <span>
          <span className="text-jarvis-text">{ai.data.remainingCredits.toLocaleString()}</span>{' '}
          credits
        </span>
      )}
      {voice.data && (
        <span>
          <span className="text-jarvis-text">{voice.data.remaining.toLocaleString()}</span> min
        </span>
      )}
    </a>
  )
}
